import type { StatePorts } from '@vitesse/statechart-host';
import { isOutsidePath, type ModelView } from '../../../core/domain/model';
import { machineRows, type MachineRow } from './machine-rows';

/*
 * The ghost tree of the Statechart tile, as data (docs/state-subgraph-plan.md). It hangs under 1
 * root state. Level 1 is the machines that the code of the state uses, with the ports on the line
 * from the state to each machine, and the PLC paths of each machine. Level 2 is the statechart of
 * each of those machines, with its live states. No Lit and no i18n here, as in machine-graph.ts:
 * the component gives the words and draws, and a node test can call this.
 *
 * The ports come from `statePorts` of the root's text, which reads what the code of the state can
 * use. A child that the state does not use is not in the tree, though it runs in every root state.
 */

/** 1 port on the line from the state to a machine. */
export interface TreePort {
  port: string;
  /** 'read': the state reads it, and the line points up to the state. 'write': the state writes it. */
  direction: 'read' | 'write';
  /** The literal that the state writes, as text, when each write in the state is that literal. */
  value?: string;
}

/** A PLC path of a machine: an input that the path feeds, or an output that drives the path. */
export interface TreePlc {
  port: string;
  path: string;
  direction: 'in' | 'out';
}

/** Level 2: the statechart of a machine, and where its machines are now. */
export interface TreeChart {
  /** The template's text; '' when the relay did not send it. */
  scxml: string;
  /** The active state ids of a single child. Empty for an array, and when the model is not active. */
  active: string[];
  /** For an array: how many of its machines are in each state id. Empty when the model is not active. */
  counts?: Record<string, number>;
}

/** Level 1: a machine that the state uses. */
export interface TreeMachine {
  /** The child's name, which is also its path in the group. */
  name: string;
  template: string;
  ports: TreePort[];
  plc: TreePlc[];
  /** Why a single child faulted, '' when it did not. An array gives its count in `array.faulted`. */
  fault: string;
  /** Present for an array child only. */
  array?: { count: number; running: number; faulted: number; stopped: number };
  chart: TreeChart;
}

/** A leaf of level 1: an input of the root that the state reads, or an output of the root that it writes. */
export interface TreeLeaf {
  name: string;
  direction: 'read' | 'write';
  /** The literal written, as for `TreePort.value`. */
  value?: string;
  /** Its wiring: the path that feeds the input, or the PLC path that the output drives. '' when it has none. */
  path: string;
}

export interface StateTree {
  state: string;
  /** The machines that the state only reads first, then the machines that it writes. Each group in the descriptor's order. */
  machines: TreeMachine[];
  leaves: TreeLeaf[];
}

const statesOf = (state: string): string[] =>
  state
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

/** The child of a `<child>/<port>` path, and the port. */
function split(path: string): [string, string] {
  const at = path.indexOf('/');
  return [path.slice(0, at), path.slice(at + 1)];
}

function plcOf(r: MachineRow): TreePlc[] {
  const plc: TreePlc[] = [];
  for (const p of r.inputs)
    if (p.constant === undefined && isOutsidePath(p.path)) plc.push({ port: p.name, path: p.path, direction: 'in' });
  for (const p of r.outputs) if (p.path) plc.push({ port: p.name, path: p.path, direction: 'out' });
  return plc;
}

function chartOf(r: MachineRow, scxml: string, active: boolean): TreeChart {
  if (!r.array) return { scxml, active: statesOf(r.state) };
  const counts: Record<string, number> = {};
  if (active) {
    for (const cell of r.array.machines ?? []) for (const id of statesOf(cell.state)) counts[id] = (counts[id] ?? 0) + 1;
  }
  return { scxml, active: [], counts };
}

/**
 * The tree under the root state `stateId`. `ports` is `statePorts` of the root's text, `templates`
 * is each template's text by its name, and `active` is `isModelActive(m.status)`. Null when the
 * root's text has no state `stateId`.
 */
export function stateTree(
  m: ModelView,
  active: boolean,
  ports: ReadonlyMap<string, StatePorts>,
  templates: Readonly<Record<string, string>>,
  stateId: string,
): StateTree | null {
  const used = ports.get(stateId);
  if (!used) return null;

  const touched = new Map<string, TreePort[]>();
  const add = (path: string, port: TreePort): void => {
    const [child] = split(path);
    const list = touched.get(child) ?? [];
    list.push(port);
    touched.set(child, list);
  };
  for (const path of used.reads) add(path, { port: split(path)[1], direction: 'read' });
  for (const w of used.writes)
    add(w.path, { port: split(w.path)[1], direction: 'write', ...(w.value !== undefined ? { value: w.value } : {}) });

  // Each array open, so that its row has a cell for each machine to count.
  const arrays = new Set(m.children.filter((c) => c.count !== undefined && touched.has(c.name)).map((c) => c.name));
  const [rootRow, ...childRows] = machineRows(m, active, arrays);
  const readers: TreeMachine[] = [];
  const written: TreeMachine[] = [];
  for (const r of childRows) {
    const list = touched.get(r.name);
    if (!list) continue;
    const machine: TreeMachine = {
      name: r.name,
      template: r.template,
      ports: list,
      plc: plcOf(r),
      fault: r.fault,
      chart: chartOf(r, templates[r.template] ?? '', active),
    };
    if (r.array) machine.array = { count: r.array.count, running: r.array.running, faulted: r.array.faulted, stopped: r.array.stopped };
    (list.some((p) => p.direction === 'write') ? written : readers).push(machine);
  }

  const wired = (list: typeof rootRow.inputs, name: string): string => list.find((p) => p.name === name)?.path ?? '';
  const leaves: TreeLeaf[] = [
    ...used.inputs.map((name): TreeLeaf => ({ name, direction: 'read', path: wired(rootRow.inputs, name) })),
    ...used.outputs.map((o): TreeLeaf => ({
      name: o.name,
      direction: 'write',
      path: wired(rootRow.outputs, o.name),
      ...(o.value !== undefined ? { value: o.value } : {}),
    })),
  ];
  return { state: stateId, machines: [...readers, ...written], leaves };
}
